/**
 * Shared reading-time estimation for body content.
 *
 * Fundamentals, builds, lessons and articles all share the same
 * {@link LessonBlock} body model, so the "how many words, divided by reading
 * speed" estimate is identical across them. Centralised here so the page
 * renderers ({@link ../components/pages.tsx}) and any other consumer compute it
 * one way instead of re-deriving it per content type.
 */
import type { LessonBlock } from './catalog'

/** Words an average adult reads per minute - the standard 200 wpm estimate. */
const WORDS_PER_MINUTE = 200

/** The shortest reading time we ever show, so trivial pages never read "0 min". */
const MIN_READING_MINUTES = 2

/**
 * Flatten a single rich body block to its plain text for word counting:
 * paragraphs contribute their text, bullet lists their items, code blocks the
 * code itself.
 */
export function blockText(block: LessonBlock): string[] {
  if (block.type === 'paragraph') return [block.text]
  if (block.type === 'bullets') return block.items
  return [block.code]
}

/**
 * Estimate reading time, in whole minutes, for a set of body strings. Splits on
 * whitespace to count words, divides by {@link WORDS_PER_MINUTE} and clamps to a
 * sensible floor.
 */
export function estimateReadingMinutes(texts: string[]): number {
  const words = texts.join(' ').split(/\s+/u).length
  return Math.max(MIN_READING_MINUTES, Math.round(words / WORDS_PER_MINUTE))
}
